import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type {
	CreateRecordProperties,
	PopupData,
	RequestError,
	Service,
	ValidationErrors,
} from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { equal, isAlive } from 'webapp/utils/common';

import setField from 'webapp/helpers/set-field';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import type { OptionRaw } from 'webapp/components/form/select';
import FormSelectComponent from 'webapp/components/form/select';
import FormTextareaComponent from 'webapp/components/form/textarea';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import Report from 'webapp/models-custom/report';
import type Template from 'webapp/models-custom/template';
import type TemplateRecord from 'webapp/models/template';

export default class TemplatesPublicPopupEditComponent extends PopupInnerModalComponent<PopupData.Template.Public.Edit> {
	@service('constants') declare constantsService: Service.Constants;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;

	@tracked errors: ValidationErrors<'categories' | 'title'> = {};

	@tracked _templateModel: Template | null = null; // if there is template existing already
	templateModel!: Template;

	// Creator
	@tracked createdByName: string | null = null;
	@tracked createdByUrl: string | null = null;
	@tracked createdByAvatar: string | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const model = this.model,
			objectId = model.numericId!;

		let existingModel = null;
		if (model instanceof Board) {
			existingModel = this.mmTemplatesService.boardPublicTemplateModels.find(
				(item) => item.objectId === objectId,
			);
		} else if (model instanceof Report) {
			existingModel = this.mmTemplatesService.reportPublicTemplateModels.find(
				(item) => item.objectId === objectId,
			);
		} else {
			existingModel = this.mmTemplatesService.boardLoopPublicTemplateModels.find(
				(item) => item.objectId === objectId,
			);
		}

		if (existingModel) {
			this._templateModel = existingModel;
			this.templateModel = this._templateModel;
		} else {
			const properties: CreateRecordProperties<TemplateRecord> = {
				objectId,
			};

			if (model instanceof Board) {
				properties.title = model.name;
				this.templateModel =
					this.mmTemplatesService.createNewBoardPublicTemplateModel(properties);
			} else if (model instanceof BoardLoop) {
				properties.title = model.title;
				this.templateModel =
					this.mmTemplatesService.createNewBoardLoopPublicTemplateModel(properties);
			} else {
				properties.title = model.title;
				this.templateModel =
					this.mmTemplatesService.createNewReportPublicTemplateModel(properties);
			}
		}

		this.createdByName = this.templateModel.createdBy.name;
		this.createdByAvatar = this.templateModel.createdBy.avatar;
		this.createdByUrl = this.templateModel.createdBy.url;
	}

	get model(): Board | Report | BoardLoop {
		return this.popupData.model;
	}

	get title(): string {
		return `${this._templateModel ? 'Edit' : 'Save as'}  Public template`;
	}

	get isDeleteBtnShown(): boolean {
		return !!this._templateModel && !this.submitInProgress;
	}

	get availableCategories(): OptionRaw<number>[] {
		const categories: LabelValuePairNumeric[] = this.constantsService.templateCategories,
			groupedTemplateSubcategories: OptionRaw<number>[] = [];

		categories.forEach((category) => {
			if (category.value % 100 !== 0) {
				const parentCategoryId = Math.floor(category.value / 100) * 100,
					parentCategory = categories.find((item) => item.value === parentCategoryId);

				if (parentCategoryId) {
					groupedTemplateSubcategories.push({
						value: category.value,
						label: category.label,
						group: parentCategory?.label,
					});
				}
			}
		});

		return groupedTemplateSubcategories;
	}

	@action
	changeCategory(value: number[]): void {
		this.templateModel.categories = value;
		this.reposition();
	}

	@action
	save() {
		const templateModel = this.templateModel,
			isNew = !this._templateModel;

		const createdBy = {
			name: this.createdByName,
			url: this.createdByUrl,
			avatar: this.createdByAvatar,
		};

		if (!equal(templateModel.createdBy, createdBy)) {
			templateModel.createdBy = createdBy;
		}

		if (!this.submitInProgress && templateModel.validate()) {
			this.submitInProgress = true;

			if (isNew) {
				// async flow for creation
				this.saveNew();
			} else {
				// sync flow for editing
				this.saveExisting();
			}
		}
	}

	private saveNew(): void {
		const templateModel = this.templateModel;
		const data = {
			template: templateModel.toSerializedJSON(),
		};

		const loadingLayer = this.infolayerService.loading('Creating template...');

		this.requestService
			.apiWithAsyncReply('POST', '/templates', data, {}, (_payload) => {
				// store push handled automatically by socket.ts triggerBgEventDone
				loadingLayer.ok('Template created!');

				this.mmTemplatesService.trackEvent(templateModel, TrackingEvent.TEMPLATE_CREATED);

				return true;
			})
			.catch((errorData: RequestError) => {
				loadingLayer.close();
				this.requestService.showAllValidationErrors(errorData);
			})
			.finally(() => {
				this.submitInProgress = false;
			});

		this.close();
	}

	private saveExisting(): void {
		const templateModel = this.templateModel;

		templateModel
			.save()
			.then((template: TemplateRecord) => {
				this.infolayerService.ok('Template data updated!');
				this.mmTemplatesService.trackEvent(template, TrackingEvent.TEMPLATE_EDITED);

				if (isAlive(this)) {
					this.close();
				}
			})
			.finally(() => {
				if (isAlive(this)) {
					this.submitInProgress = false;
				}
			})
			.catch(this.requestService.showGenericValidationError.bind(this.requestService));
	}

	@action
	delete(): void {
		this.submitInProgress = true;
		void this.mmTemplatesService
			.deleteTemplate(this.templateModel, true)
			.then(() => {
				this._templateModel = null; // to hide delete btn after successful template remove
			})
			.finally(() => {
				this.submitInProgress = false;
			});
	}
	<template>
		<div class="title grey-on-transparent">
			<CommonUiIconComponent @name="FolderOpen" @size="lg" />
			{{this.title}}
		</div>

		<div class="content">
			<div class="row">
				<label>Title</label>
				<div class="right">
					<FormInputComponent
						name="title"
						disabled={{this.submitInProgress}}
						maxlength="50"
						required="true"
						@value={{this.templateModel.title}}
						@onEmpty={{setField target=this.templateModel property="title"}}
					/>
					<RowErrorComponent @items={{this.templateModel.errors.title}} />
				</div>
			</div>

			<div class="row categories">
				<label>Subcategories</label>
				<div class="right">
					<FormSelectComponent
						name="categories"
						disabled={{this.submitInProgress}}
						@selected={{this.templateModel.categories}}
						@options={{this.availableCategories}}
						@isMultiple={{true}}
						@onChange={{this.changeCategory}}
					/>
					<RowErrorComponent @items={{this.templateModel.errors.categories}} />
				</div>
			</div>

			<div class="row">
				<label>Short description</label>
				<div class="right">
					<FormTextareaComponent
						name="description"
						disabled={{this.submitInProgress}}
						maxlength="250"
						@value={{this.templateModel.description}}
					/>
				</div>
			</div>

			<div class="row">
				<label>Keywords</label>
				<div class="right keyword">
					<FormTextareaComponent
						name="keywords"
						disabled={{this.submitInProgress}}
						maxlength="512"
						@value={{this.templateModel.keywords}}
					/>
				</div>
			</div>

			<hr />

			<div class="row">
				<label>Authorship</label>
				<div class="right">
					<FormInputComponent
						name="creator_name"
						disabled={{this.submitInProgress}}
						maxlength="50"
						@value={{this.createdByName}}
						placeholder="Name"
						@onEmpty={{setField target=this property="createdByName"}}
					/>
				</div>
			</div>

			<div class="row">
				<label></label>
				<div class="right">
					<FormInputComponent
						name="creator_url"
						disabled={{this.submitInProgress}}
						@type="url"
						@value={{this.createdByUrl}}
						placeholder="URL"
					/>
				</div>
			</div>

			<div class="row">
				<label></label>
				<div class="right">
					<FormInputComponent
						name="creator_avatar"
						disabled={{this.submitInProgress}}
						@value={{this.createdByAvatar}}
						placeholder="Avatar"
						@onEmpty={{setField target=this property="createdByAvatar"}}
					/>
				</div>
			</div>

			<div class="controls2">
				{{#if this.isDeleteBtnShown}}
					<div class="controls2__left">
						<SubmitButtonComponent
							@text="Remove as a template"
							@icon="Trash"
							@theme="red-empty"
							@noDelay={{true}}
							@onTrigger={{this.delete}}
						/>
					</div>
				{{/if}}
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>
		</div>
	</template>
}
